import moment from "moment-timezone";

import { redactLoreTerms } from "src/shared/utils/loreReveal";

export const DASHBOARD_TIME_ZONE = "America/Toronto";
export const MAX_DASHBOARD_EVENT_SLOTS = 11;

export type DashboardScheduleEvent = {
  id: number;
  title: string;
  start_time: string;
  end_time: string;
  location: string;
  tags: string[];
  description?: string | null;
  links?: string[] | null;
};

export type LoreStage = {
  threshold: number;
  title: string;
  body: string;
};

type LumaMilestone = {
  progress: number;
  timestamp: number;
};

const getTorontoTimestamp = (dateTime: string) =>
  moment.tz(dateTime, DASHBOARD_TIME_ZONE).valueOf();

export const HACKING_START_TIMESTAMP = getTorontoTimestamp(
  "2026-09-19T00:00:00"
);
export const HACKING_END_TIMESTAMP = getTorontoTimestamp("2026-09-20T08:00:00");

// Slack confirms the four target milestones and continuous growth. It does
// not specify the initial anchor or interpolation curve, so we anchor at the
// existing hacking start and use piecewise-linear interpolation between them.
export const LUMA_MILESTONES: LumaMilestone[] = [
  {
    progress: 0,
    timestamp: HACKING_START_TIMESTAMP,
  },
  {
    progress: 25,
    timestamp: getTorontoTimestamp("2026-09-19T12:00:00"),
  },
  {
    progress: 50,
    timestamp: getTorontoTimestamp("2026-09-19T16:00:00"),
  },
  {
    progress: 75,
    timestamp: getTorontoTimestamp("2026-09-19T20:00:00"),
  },
  {
    progress: 100,
    timestamp: getTorontoTimestamp("2026-09-20T10:00:00"),
  },
];

export const LORE_STAGES: LoreStage[] = [
  {
    threshold: 0,
    title: "The Liminal Plains",
    body: "Shadows loom over the horizon. Complete activities, join workshops, and submit hacker projects to trigger the luma to light up the path.",
  },
  {
    threshold: 25,
    title: "The Whispering Canopy",
    body: "25% restored! Inspiration is igniting across the woods. Keep up the momentum to restore luma and drive the Fade away!",
  },
  {
    threshold: 50,
    title: "The Sunken Vaults",
    body: "50% restored! Deep in the water, a collective pulse of luma surges through the dark waters. The Fade is fracturing under our shared focus!",
  },
  {
    threshold: 75,
    title: "The Twilight Peaks",
    body: "75% restored! We’re soaring through the dark with peak momentum. Finish those projects and light up the sky!",
  },
  {
    threshold: 100,
    title: "The Vibrant Cosmos",
    body: "Fade Defeated! The sky is ablaze with full energy. Every project and action brought the luma back to the realm!",
  },
];

export const getLumaProgress = (now: Date) => {
  const nowTimestamp = now.getTime();
  const firstMilestone = LUMA_MILESTONES[0];
  const finalMilestone = LUMA_MILESTONES[LUMA_MILESTONES.length - 1];

  if (nowTimestamp <= firstMilestone.timestamp) return firstMilestone.progress;
  if (nowTimestamp >= finalMilestone.timestamp) return finalMilestone.progress;

  for (let index = 1; index < LUMA_MILESTONES.length; index += 1) {
    const nextMilestone = LUMA_MILESTONES[index];
    if (nowTimestamp <= nextMilestone.timestamp) {
      const previousMilestone = LUMA_MILESTONES[index - 1];
      const intervalDuration =
        nextMilestone.timestamp - previousMilestone.timestamp;
      const intervalElapsed = nowTimestamp - previousMilestone.timestamp;
      const intervalProgress = intervalElapsed / intervalDuration;

      return (
        previousMilestone.progress +
        intervalProgress * (nextMilestone.progress - previousMilestone.progress)
      );
    }
  }

  return finalMilestone.progress;
};

export const getLoreStage = (progress: number, now: Date) => {
  const lore = LORE_STAGES.reduce(
    (currentStage, stage) =>
      progress >= stage.threshold ? stage : currentStage,
    LORE_STAGES[0]
  );

  return { ...lore, body: redactLoreTerms(lore.body, now) };
};

const isDashboardEvent = (event: DashboardScheduleEvent) =>
  !event.tags.includes("archived") &&
  !event.tags.includes("banner") &&
  !event.tags.includes("organizer_only") &&
  !event.tags.includes("organizers_only");

const sortByStartTime = (
  firstEvent: DashboardScheduleEvent,
  secondEvent: DashboardScheduleEvent
) =>
  new Date(firstEvent.start_time).getTime() -
    new Date(secondEvent.start_time).getTime() ||
  firstEvent.id - secondEvent.id;

export const splitDashboardEvents = (
  events: DashboardScheduleEvent[],
  now: Date
) => {
  const nowTimestamp = now.getTime();
  const visibleEvents = events.filter(isDashboardEvent).sort(sortByStartTime);

  return {
    happeningNow: visibleEvents.filter((event) => {
      const startTimestamp = new Date(event.start_time).getTime();
      const endTimestamp = new Date(event.end_time).getTime();
      return startTimestamp <= nowTimestamp && nowTimestamp < endTimestamp;
    }),
    upNext: visibleEvents.filter(
      (event) => new Date(event.start_time).getTime() > nowTimestamp
    ),
  };
};

export const getDashboardSnapshot = (
  events: DashboardScheduleEvent[],
  now: Date
) => {
  const progress = getLumaProgress(now);

  return {
    progress,
    lore: getLoreStage(progress, now),
    ...splitDashboardEvents(events, now),
  };
};

export const capDashboardEventSections = (
  happeningNow: DashboardScheduleEvent[],
  upNext: DashboardScheduleEvent[]
) => {
  const minimumHappeningNowEvents = happeningNow.length > 0 ? 1 : 0;
  const minimumUpNextEvents = upNext.length > 0 ? 1 : 0;
  let visibleHappeningNowCount = minimumHappeningNowEvents;
  let visibleUpNextCount = minimumUpNextEvents;

  allocation: for (
    let happeningNowCount = Math.min(
      happeningNow.length,
      MAX_DASHBOARD_EVENT_SLOTS
    );
    happeningNowCount >= minimumHappeningNowEvents;
    happeningNowCount -= 1
  ) {
    for (
      let upNextCount = Math.min(upNext.length, MAX_DASHBOARD_EVENT_SLOTS);
      upNextCount >= minimumUpNextEvents;
      upNextCount -= 1
    ) {
      const hiddenHappeningNowCount = happeningNow.length - happeningNowCount;
      const hiddenUpNextCount = upNext.length - upNextCount;
      const renderedRows =
        Math.max(happeningNowCount, 1) +
        Math.max(upNextCount, 1) +
        (hiddenHappeningNowCount > 0 ? 1 : 0) +
        (hiddenUpNextCount > 0 ? 1 : 0);

      if (renderedRows <= MAX_DASHBOARD_EVENT_SLOTS) {
        visibleHappeningNowCount = happeningNowCount;
        visibleUpNextCount = upNextCount;
        break allocation;
      }
    }
  }

  const visibleHappeningNow = happeningNow.slice(0, visibleHappeningNowCount);
  const visibleUpNext = upNext.slice(0, visibleUpNextCount);

  return {
    visibleHappeningNow,
    visibleUpNext,
    hiddenHappeningNowCount: happeningNow.length - visibleHappeningNow.length,
    hiddenUpNextCount: upNext.length - visibleUpNext.length,
  };
};

export const formatCompactDuration = (milliseconds: number) => {
  if (milliseconds > 0 && milliseconds < 60000) return "<1m";

  const totalMinutes = Math.max(Math.floor(milliseconds / 60000), 0);
  if (totalMinutes < 1) return "0m";

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) return `${minutes}m`;
  return `${hours}h${minutes > 0 ? `${minutes}m` : ""}`;
};

const formatCountdownDuration = (milliseconds: number) => {
  const totalSeconds = Math.max(Math.ceil(milliseconds / 1000), 0);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds]
    .map((value) => value.toString().padStart(2, "0"))
    .join(":");
};

export const getHackingCountdown = (now: Date) => {
  const nowTimestamp = now.getTime();

  if (nowTimestamp < HACKING_START_TIMESTAMP) {
    return {
      label: "Hacking starts in",
      value: formatCountdownDuration(HACKING_START_TIMESTAMP - nowTimestamp),
    };
  }

  if (nowTimestamp >= HACKING_END_TIMESTAMP) {
    return { label: "Hacking has ended!", value: "00:00:00" };
  }

  return {
    label: "Hacking ends in",
    value: formatCountdownDuration(HACKING_END_TIMESTAMP - nowTimestamp),
  };
};
